<template>
	<div>
      <input type="hidden" :value="changeTitle()" />
  		<router-view/>
		<div v-if="! cs.disableLoader">
			<!-- One loading screen, everywhere. The kiosk had two of its own
			     for a while — a bar across the top, a ring in the middle — and
			     they are gone: a guest at a tablet should meet the same screen
			     the rest of the product uses, and its text is the reason it
			     wins. "Do not back or refresh the page" is the one thing worth
			     saying to somebody standing at a shared device, and neither a
			     bar nor a bare spinner says it.

			     What differs on a kiosk route is only what raises it: the
			     counted + hysteresis loader (services/loader.js) rather than
			     the site-wide boolean, which blinks off between the sequential
			     calls a kiosk screen boots with. The kiosk's own "Now Starting"
			     panel is separate again — it belongs to the start screen, which
			     draws it over its own boot and silences this one. -->
			<loading :active="overlayActive"
				@update:active="onOverlayUpdate"
				:can-cancel="false"
				:is-full-page="true"
				loader="bars"
				:width="70"
				:height="70"
				color="#50a4f2"
				:z-index="overlayZIndex"
                opacity="0.8">
			<template slot="after">
                <div class="loading-text-container">
                    <div class="custom-loading-text title">
                        <strong>{{ $t("processing.title") }}</strong>
                    </div>
                    <div class="custom-loading-text sub-text">
                        <strong>{{ $t("processing.text") }}</strong>
                    </div>
                </div>
            </template>
			</loading>
		</div>
  	</div>
</template>

<style scoped>
::v-deep .vld-icon {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100%;
}

.loading-text-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 20px; /* Space between the bars and the text */
}

.custom-loading-text {
    text-align: center;
    color: #50a4f2;
    width: 70%;
	
}
</style>

<!-- Unscoped on purpose: this one is applied to <html>/<body>, which no scoped
     rule can reach. -->
<style>
/* iPad: a `position: fixed` overlay does not stop the document under it from
   scrolling. A guest resting a thumb on the submit overlay would drag the form
   behind it into view, or bounce the whole thing on the rubber-band — on a
   tablet the loading screen looked like it was coming apart. Locking the
   document is what actually holds it still; `touch-action` and
   `overscroll-behavior` stop the drag and the bounce from ever starting. */
html.kiosk-scroll-locked,
html.kiosk-scroll-locked body {
    overscroll-behavior: none;
}

html.kiosk-scroll-locked body {
    position: fixed;
    left: 0;
    right: 0;
    width: 100%;
    overflow: hidden;
    touch-action: none;
}
</style>

<script>
	import { mapState } from 'vuex';
	import { isKioskRoute } from '@/modules/checkin-facility/pages/checkin/kiosk/kiosk_config';
	import loaderController from '@/services/loader';
	// Import component
    import Loading from 'vue-loading-overlay';
    // Import stylesheet
    import 'vue-loading-overlay/dist/vue-loading.css';

	// The check-in form's submit calls open this modal themselves. On every
	// other page it is left to do its own job; on a kiosk route it also marks
	// the span of a submit for the loader, so the overlay above covers the
	// write even when the submit was started somewhere this file cannot see —
	// the phone form, the pre-check-in confirm, a payment iframe. Nothing at
	// the call sites changes.
	const PROCESSING_MODAL_ID = 'processing-modal';

	// Above that modal (z-index 10000), because on a kiosk the overlay stands in
	// for it rather than beside it: the modal is left where it is and covered,
	// instead of two spinners stacking up for one submit. Off every other page,
	// where the overlay keeps the default z-index it has always had.
	const KIOSK_OVERLAY_Z_INDEX = 10050;

	export default {
		components: {
            Loading
    },
    data() {
      return {
        // Whether the hold below is currently open, so it is released once and
        // only by the code that took it.
        processing_modal_held: false,
        // Scroll-lock bookkeeping — see lockScroll().
        scroll_locked: false,
        locked_scroll_y: 0
      };
    },
    mounted() {
      this.$root.$on('bv::modal::show', this.onModalShow);
      this.$root.$on('bv::modal::hidden', this.onModalHidden);
    },
    beforeDestroy() {
      this.$root.$off('bv::modal::show', this.onModalShow);
      this.$root.$off('bv::modal::hidden', this.onModalHidden);
      this.releaseProcessingModal();
      this.unlockScroll();
    },
    watch: {
      // A form that navigates away with the modal still open never emits
      // `hidden`, and an unreleased hold would keep the overlay up until the
      // controller's own 30s safety net. The route change is the end of that
      // span either way.
      $route() {
        this.releaseProcessingModal();
      },

      // The overlay covers the whole tablet, so the document under it is held
      // still for as long as it is up — see lockScroll().
      overlayActive: {
        immediate: true,
        handler(active) {
          if (active && this.isKiosk) this.lockScroll();
          else this.unlockScroll();
        }
      }
    },
    methods: {
      onModalShow(bv_event, modal_id) {
        if (modal_id !== PROCESSING_MODAL_ID) return;
        if (! this.isKiosk || this.processing_modal_held) return;

        this.processing_modal_held = true;
        loaderController.beginProcessing();
      },
      onModalHidden(bv_event, modal_id) {
        if (modal_id !== PROCESSING_MODAL_ID) return;
        this.releaseProcessingModal();
      },
      // Deliberately not gated on isKiosk: the route may already have changed
      // by the time the hold is given back.
      releaseProcessingModal() {
        if (! this.processing_modal_held) return;

        this.processing_modal_held = false;
        loaderController.endProcessing();
      },

      // `.sync` in template form: the component writes `active` back when it
      // takes itself down. On a kiosk the loader owns that flag and nothing
      // else may clear it — the overlay has `can-cancel` off, so the only write
      // that could ever arrive is the component agreeing with what it was told.
      onOverlayUpdate(active) {
        if (this.isKiosk) return;
        this.cs.isLoading = active;
      },

      // Hold the document still under the submit overlay. `position: fixed` on
      // the body is the part iPadOS actually obeys, and it costs the scroll
      // position — so that is remembered here and put back on unlock, which
      // matters because a guest meets this overlay partway down a form they
      // have just filled in.
      lockScroll() {
        if (this.scroll_locked) return;

        this.scroll_locked = true;
        this.locked_scroll_y = window.pageYOffset || document.documentElement.scrollTop || 0;
        document.body.style.top = `-${this.locked_scroll_y}px`;
        document.documentElement.classList.add('kiosk-scroll-locked');
      },

      unlockScroll() {
        if (! this.scroll_locked) return;

        const restore_to = this.locked_scroll_y;

        this.scroll_locked = false;
        this.locked_scroll_y = 0;
        document.documentElement.classList.remove('kiosk-scroll-locked');
        document.body.style.top = '';

        // Next tick, not now: the overlay puts an `overflow: hidden` of its own
        // on the body while it is up and takes it off in a watcher of its own,
        // and a scroll asked for before that one has run lands nowhere.
        this.$nextTick(() => window.scrollTo(0, restore_to));
      },
      changeTitle() {
        const title = {
          en: 'QR Code and Self Check-In',
          ja: 'QRコード x セルフチェックインシステム',
          ko: 'QR Code and Self Check-In',
          "zh-CN": 'QR Code and Self Check-In',
          "zh-TW": 'QR Code and Self Check-In',
        }

        document.title = title[this.$i18n.locale]
      }
    },
		computed: {
			...mapState('checkin-site', {
				cs : state => state
			}),
			// Which controller raises the overlay on this route. The screen it
			// draws is the same one either way.
			isKiosk() {
				return isKioskRoute(this.$route);
			},
			loader() {
				return loaderController;
			},
			// Off the kiosk, the site-wide boolean every request flips. On it,
			// the two things the controller distinguishes: work in flight, and
			// a form being validated and submitted — one screen for both,
			// because they are the same news to the guest waiting on them.
			overlayActive() {
				if (! this.isKiosk) return this.cs.isLoading;
				return this.loader.visible || this.loader.processing;
			},
			overlayZIndex() {
				return this.isKiosk ? KIOSK_OVERLAY_Z_INDEX : undefined;
			}
		}
	}
</script>